import { children, pathText } from './ast.js';
import { FormulaErrorCodes, formulaError } from './errors.js';
import { isCollection } from './types.js';
/**
 * FL-W01 (§4.7, §5): `all(coll, pred)` is true for an empty collection, which is rarely what a formula means
 * ("every row is signed" of a signing with no rows). The warning is reported for every `all()` that no
 * `count(coll) > 0` guards: a guard is such a comparison on the same collection in the other operand of an
 * enclosing `&&`, or in the test of an enclosing `?:` or `if()` whose then-branch holds the `all()`. Runs on a
 * checked tree, since a collection is known only by the checker's type of the reference.
 */
export function warnUnguardedAll(root, nodeTypes, errors) {
    new GuardWalk(nodeTypes, errors).walk(root, new Set());
}
/** `count(coll) > 0` in its equivalent spellings: `> 0`, `>= 1`, `!= 0`, and mirrored. */
const GUARDS = new Set(['>:0', '>=:1', '!=:0']);
const MIRRORED = { '<': '>', '<=': '>=', '!=': '!=' };
class GuardWalk {
    constructor(nodeTypes, errors) {
        this.nodeTypes = nodeTypes;
        this.errors = errors;
    }
    walk(node, guarded) {
        const all = this.aggregateCall(node, 'all');
        if (all && !guarded.has(all)) {
            const collection = node.args[0];
            this.errors.push(formulaError(FormulaErrorCodes.AllWithoutCountGuard, node.span, `all() is true for an empty collection; guard it with count(${pathText(collection)}) > 0.`, {
                collection: pathText(collection),
            }));
        }
        if (node.kind === 'binary' && node.op === '&&') {
            this.walk(node.left, union(guarded, this.guardsOf(node.right)));
            this.walk(node.right, union(guarded, this.guardsOf(node.left)));
            return;
        }
        if (node.kind === 'cond') {
            this.walk(node.test, guarded);
            this.walk(node.then, union(guarded, this.guardsOf(node.test)));
            this.walk(node.else, guarded);
            return;
        }
        if (node.kind === 'call' && node.fn === 'if' && node.args.length === 3) {
            this.walk(node.args[0], guarded);
            this.walk(node.args[1], union(guarded, this.guardsOf(node.args[0])));
            this.walk(node.args[2], guarded);
            return;
        }
        for (const child of children(node))
            this.walk(child, guarded);
    }
    /** The collections an expression proves non-empty when it is true: `count(coll) > 0`, through `&&`. */
    guardsOf(node) {
        if (node.kind !== 'binary')
            return new Set();
        if (node.op === '&&')
            return union(this.guardsOf(node.left), this.guardsOf(node.right));
        const guarded = this.nonEmptyGuard(node);
        return guarded ? new Set([guarded]) : new Set();
    }
    /** The collection id when `node` is `count(coll …) OP literal` (or mirrored) proving at least one row. */
    nonEmptyGuard(node) {
        const left = this.aggregateCall(node.left, 'count');
        if (left && node.right.kind === 'num' && GUARDS.has(`${node.op}:${node.right.value}`))
            return left;
        const right = this.aggregateCall(node.right, 'count');
        const mirrored = MIRRORED[node.op];
        if (right && mirrored && node.left.kind === 'num' && GUARDS.has(`${mirrored}:${node.left.value}`))
            return right;
        return undefined;
    }
    /** The collection id when `node` is a call of `fn` over a collection symbol. */
    aggregateCall(node, fn) {
        if (node.kind !== 'call' || node.fn !== fn || node.args[0]?.kind !== 'ref')
            return undefined;
        const first = node.args[0];
        return first.symbol !== null && isCollection(this.nodeTypes.get(first)?.type) ? first.symbol : undefined;
    }
}
function union(a, b) {
    return b.size === 0 ? a : new Set([...a, ...b]);
}
